import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, GraduationCap, MapPin, Clock, MessageSquare, Heart, Shield, ShieldCheck, Sparkles, ChevronRight, ExternalLink, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';

const faqQuestions = [
  {
    number: "01",
    q: "Hvorfor valgte jeg at starte min egen klinik, Core Hypnose?",
    a: [
      "Jeg startede Core Hypnose i 2019 efter mange års arbejde med mennesker.",
      "Jeg havde gennem mit arbejdsliv oplevet, hvor forskelligt mennesker reagerer på livets udfordringer, og hvor vigtigt det er at blive mødt som den, man er.",
      "Med Core Hypnose fik jeg mulighed for at samle mit arbejde med mennesker, min interesse for psykologi og de terapeutiske metoder, jeg havde uddannet mig indenfor.",
      "Det, der fortsat er vigtigt for mig, er, at terapi ikke kun skal være et sted, hvor man fortæller om sine problemer. Der skal også ske noget. Man skal gerne gå derfra med en ny forståelse, en konkret erfaring eller en oplevelse af, at noget faktisk kan gøres anderledes."
    ]
  },
  {
    number: "02",
    q: "Hvad har mine mange år som pædagog lært mig om mennesker?",
    a: [
      "Jeg blev uddannet pædagog i 2003 og har blandt andet arbejdet som støttepædagog, mentor, støttekontaktperson og hjemmevejleder.",
      "Det har givet mig erfaring med mennesker i meget forskellige livssituationer.",
      "Noget af det vigtigste, jeg tager med mig, er betydningen af at møde et menneske uden på forhånd at tro, at jeg kender hele historien. To mennesker kan opleve noget, der ligner det samme problem, men have vidt forskellige behov.",
      "Jeg har også lært, at mennesker ikke altid har brug for flere gode råd. Nogle gange er der mere brug for at blive lyttet til, få stillet et spørgsmål, man ikke tidligere har overvejet, eller opdage, at man faktisk har flere muligheder, end man troede."
    ]
  },
  {
    number: "03",
    q: "Hvad var det ved metakognitiv terapi, der særligt fangede min interesse?",
    a: [
      "Noget af det, jeg finder særligt interessant ved metakognitiv terapi, er, at vi ikke behøver at undersøge og analysere alle vores tanker for at få det bedre.",
      "Vi mennesker kan bruge utrolig meget tid på at forsøge at finde svar på ting, som måske slet ikke har et endeligt svar. Hvorfor tænker jeg sådan? Hvad betyder det? Hvad nu, hvis det sker igen? Og jo mere vi forsøger at regne det hele ud, jo mere kan vi ende med at sidde fast.",
      "Metakognitiv terapi arbejder med noget andet. Nemlig vores forhold til tankerne, og hvordan vi bruger vores opmærksomhed. Det tiltaler mig, at man kan arbejde meget konkret med noget, der ellers kan føles både kompliceret og uoverskueligt."
    ]
  },
  {
    number: "04",
    q: "Hvad har jeg ændret mening om efter mere end 26 års arbejde med mennesker?",
    a: [
      "Jeg er blevet mere optaget af, at mennesker ikke nødvendigvis har brug for at forstå alt om sig selv for at skabe forandring.",
      "Vi kan bruge lang tid på at lede efter forklaringer. Hvorfor er jeg blevet sådan? Hvor kommer mine tanker fra? Hvad fortæller de om mig? Forklaringer kan være nyttige, men de er ikke altid det, der hjælper os videre.",
      "I dag er jeg især optaget af, hvad vi gør med vores tanker, og hvordan vores vaner og opmærksomhed kan være med til at fastholde problemer. Jeg er også blevet mere opmærksom på, hvor vigtigt det er ikke at gøre tingene mere komplicerede, end de behøver at være. Det enkle kan godt være fagligt solidt."
    ]
  },
  {
    number: "05",
    q: "Hvad gør jeg, hvis jeg vurderer, at jeg ikke er den rette til at hjælpe?",
    a: [
      "Jeg synes, det er vigtigt at være ærlig om, hvad jeg kan hjælpe med, og hvad der ligger uden for mine faglige kompetencer.",
      "Det er ikke alle problemstillinger, der egner sig til den behandling, jeg tilbyder. Derfor bruger jeg også den første kontakt og den indledende samtale til at undersøge, hvad der er behov for.",
      "Hvis jeg vurderer, at en anden fagperson eller en anden form for behandling vil være mere relevant, vil jeg hellere sige det end begynde et forløb, som jeg ikke mener er det rigtige. For mig er faglig ansvarlighed også at kende sine begrænsninger."
    ]
  },
  {
    number: "06",
    q: "Hvorfor er det vigtigt for mig også at supervisere andre terapeuter?",
    a: [
      "Jeg er certificeret metakognitiv supervisor, og supervision er en naturlig forlængelse af mit arbejde med mennesker.",
      "Som terapeut kan man også komme til at sidde fast i sine egne tanker om, hvad der er den rigtige måde at hjælpe på. Her kan det være værdifuldt at få et andet blik på situationen.",
      "I supervision arbejder jeg blandt andet med at skabe overblik, undersøge terapeutiske valg og stille spørgsmål, der åbner for nye perspektiver.",
      "Jeg synes, det er interessant, hvordan et enkelt spørgsmål nogle gange kan få os til at se noget, vi ellers havde overset. Og så mener jeg, at man aldrig bliver færdig med at udvikle sig fagligt. Heller ikke efter mange års erfaring."
    ]
  },
  {
    number: "07",
    q: "Hvad er vigtigst for mig, når jeg møder et menneske for første gang?",
    a: [
      "Det vigtigste for mig er at møde mennesket, før vi begynder at tale om metoder og behandling.",
      "Jeg er nysgerrig på, hvem du er, hvordan din hverdag ser ud, og hvad der har fået dig til at søge hjælp.",
      "Jeg tror, vi nogle gange kan være lidt for hurtige til at sætte mennesker i bestemte kategorier. Angst, stress, depression eller noget helt fjerde. Men der er jo et helt menneske bag de ord.",
      "Du behøver ikke have en færdig forklaring på, hvorfor du har det, som du har det. Og du skal heller ikke fortælle hele din livshistorie for at komme i gang.",
      "Jeg vil gerne forstå, hvad der fylder hos dig, og hvad du ønsker skal være anderledes. Derefter kan vi sammen finde ud af, hvordan vi bedst arbejder videre."
    ]
  },
  {
    number: "08",
    q: "Hvornår oplever jeg, at mit arbejde virkelig giver mening?",
    a: [
      "Noget af det, jeg finder mest meningsfuldt, er, når et menneske begynder at opdage, at tanker ikke behøver at bestemme, hvordan resten af dagen skal være.",
      "Det kan være en person, der har brugt meget tid på at bekymre sig, men som begynder at opleve, at bekymringerne godt kan være der, uden at man behøver at følge dem.",
      "Eller en person, som opdager, at man ikke skal have fuldstændig ro i hovedet for at kunne gøre noget, man gerne vil.",
      "Jeg er optaget af de små forandringer, fordi de nogle gange kan få stor betydning i hverdagen. For mig er det vigtigt, at det, vi arbejder med i terapien, også giver mening, når man går ud ad døren."
    ]
  },
  {
    number: "09",
    q: "Hvad vil jeg gerne have, at et menneske tager med sig efter at have mødt mig?",
    a: [
      "Først og fremmest vil jeg gerne have, at man har oplevet at blive mødt ordentligt. At der har været plads til at fortælle, hvordan man har det, uden at skulle præstere eller forklare alting perfekt.",
      "Jeg vil også gerne have, at man går derfra med en oplevelse af, at der findes muligheder, som man måske ikke havde fået øje på før.",
      "Jeg tror ikke, at alle problemer kan løses med et enkelt spørgsmål eller en bestemt øvelse. Men jeg tror på værdien af at opdage, at vi ikke nødvendigvis behøver at reagere på vores tanker på den samme måde, som vi plejer.",
      "Hvis man tager lidt mere frihed og tillid til sine egne muligheder med sig, er det et godt udgangspunkt."
    ]
  },
  {
    number: "10",
    q: "Hvad giver mig selv ro, glæde og energi uden for arbejdet?",
    a: [
      "Jeg er nysgerrig af natur og kan godt lide at fordybe mig i ting, der interesserer mig.",
      "Jeg synes, der kan være noget tilfredsstillende ved at undersøge, hvordan noget fungerer, og finde sin egen måde at gøre tingene på.",
      "Jeg er også optaget af tanken om, at ro ikke nødvendigvis er noget, vi skal skabe ved at kontrollere alt det, der foregår i hovedet.",
      "Nogle gange handler det mere om at give sin opmærksomhed til det, man er i gang med, og lade resten være. Det er en tilgang, der interesserer mig både fagligt og personligt."
    ]
  }
];

const OmLars = () => {
  // Support folding each of the 10 questions individually
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Helmet>
        <title>Lars Nissen Corell – Psykoterapeut MPF | København & Valby</title>
        <meta name="description" content="Lars Nissen Corell – Psykoterapeut MPF. Certificeret metakognitiv psykoterapeut, Neokognitivt Institut, Level IV – højeste niveau, samt supervisor og hypnoterapeut i Valby." />
        <meta property="og:title" content="Lars Nissen Corell – Psykoterapeut MPF | København & Valby" />
        <meta property="og:description" content="Lars Nissen Corell – Psykoterapeut MPF. Certificeret metakognitiv psykoterapeut, Neokognitivt Institut, Level IV – højeste niveau, samt supervisor og hypnoterapeut i Valby." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/82128937-2b41-43f0-9af3-13a8ee842229.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lars Nissen Corell – Psykoterapeut MPF | København & Valby" />
        <meta name="twitter:description" content="Lars Nissen Corell – Psykoterapeut MPF. Certificeret metakognitiv psykoterapeut, Neokognitivt Institut, Level IV – højeste niveau, samt supervisor og hypnoterapeut i Valby." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/82128937-2b41-43f0-9af3-13a8ee842229.png" />
        <link rel="canonical" href="https://corehypnose.dk/om-lars" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden border-b border-[#E8DDCE] bg-[#FAF7F2]">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 mb-6 text-[11px] font-bold tracking-[0.2em] uppercase text-[#8C2F12] bg-[#8C2F12]/5">
              26+ års erfaring · Professionel hjælp til dig, der vil videre
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-5xl font-serif text-[#123F13] mb-4 leading-tight">
              Lars Nissen Corell – Psykoterapeut MPF
            </h1>
            <div className="text-sm md:text-lg text-[#1b4c2b] font-medium mb-6 space-y-1">
              <p>
                Certificeret metakognitiv psykoterapeut,{' '}
                <a
                  href="https://www.neokognitivtinstitut.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-4 decoration-[#9aab9f] hover:decoration-[#123F13] inline-flex items-center gap-1"
                >
                  Neokognitivt Institut
                  <ExternalLink size={13} className="inline opacity-80" />
                </a>
                , Level IV – højeste niveau
              </p>
              <p>Supervisor og hypnoterapeut</p>
            </div>
            <p className="text-lg md:text-xl text-[#3B352F] font-light leading-relaxed max-w-2xl mx-auto">
              Hos mig får du ikke bare en samtale, men et behandlingsforløb med fokus på reel forandring. 
              Jeg hjælper dig med angst, stress, depression, overtænkning og indre uro.
            </p>
            <div className="mt-8 flex justify-center">
              <Link
                to="/bestil-tid"
                className="inline-flex items-center justify-center px-8 py-3.5 bg-[#123F13] text-white text-xs md:text-sm font-bold tracking-[0.15em] uppercase hover:bg-[#1a5a1c] transition-all shadow-md hover:shadow-lg rounded-sm"
              >
                Book første behandlingssession
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Stats */}
      <section className="py-10 bg-white border-b border-[#E8DDCE]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
            <div className="text-center p-2">
              <p className="text-2xl sm:text-3xl font-serif text-[#123F13] mb-1">26+</p>
              <p className="text-[10px] sm:text-[11px] uppercase tracking-wider sm:tracking-widest text-[#3B352F]/70 font-bold">Års erfaring</p>
            </div>
            <div className="text-center p-2">
              <p className="text-2xl sm:text-3xl font-serif text-[#123F13] mb-1">Level IV</p>
              <p className="text-[10px] sm:text-[11px] uppercase tracking-wider sm:tracking-widest text-[#3B352F]/70 font-bold">Højeste NKI-niveau</p>
            </div>
            <div className="text-center p-2">
              <p className="text-2xl sm:text-3xl font-serif text-[#123F13] mb-1">MPF</p>
              <p className="text-[10px] sm:text-[11px] uppercase tracking-wider sm:tracking-widest text-[#3B352F]/70 font-bold">Dansk Psykoterapeutforening</p>
            </div>
            <div className="text-center p-2">
              <p className="text-2xl sm:text-3xl font-serif text-[#123F13] mb-1">900 timer</p>
              <p className="text-[9px] sm:text-[10px] md:text-[11px] uppercase tracking-normal sm:tracking-widest text-[#3B352F]/70 font-bold leading-tight md:whitespace-nowrap">
                Fireårig psykoterapeutuddannelse
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Om Lars Content */}
      <section className="py-20 lg:py-24 bg-[#FAF7F2]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="text-[#3B352F]/85">
                <h2 className="text-3xl font-serif text-[#123F13] mb-6">Om Lars</h2>
                <p className="mb-5 leading-relaxed text-[15px]">
                  Jeg har stor erfaring med at hjælpe mennesker, der føler sig fanget i bekymringer, grublerier, tankemylder og indre uro.
                </p>
                <p className="mb-5 leading-relaxed text-[15px]">
                  Jeg er optaget af at hjælpe mennesker til at opdage, at de ikke behøver være styret af deres tanker. Mange tror, at de skal forstå alt, analysere alt og have styr på alt for at få det bedre. Men ofte er det netop det, der holder dem fast.
                </p>
                <blockquote className="my-8 border-l-2 border-[#123F13] pl-6 py-2 italic text-lg text-[#123F13] bg-white/70">
                  "Forandringen begynder ofte, når man lærer at forholde sig anderledes til det, der foregår i sindet."
                </blockquote>
                <p className="mb-5 leading-relaxed text-[15px]">
                  I mit arbejde tager jeg blandt andet afsæt i metakognitiv terapi, hvor fokus er på de mentale strategier, der vedligeholder lidelse. Når det giver mening, inddrager jeg også hypnose som en nænsom og effektiv vej til ro, tryghed og forandring.
                </p>
                <p className="mb-5 leading-relaxed text-[15px]">
                  For mig handler behandling ikke kun om symptomer. Det handler også om, hvordan du kan få kontakten tilbage til dig selv og begynde at leve mere frit.
                </p>
                <p className="mb-5 leading-relaxed text-[15px]">
                  Det behøver ikke være stift, tungt eller kompliceret at få hjælp. Jeg lægger vægt på, at der er en stemning af tryghed, menneskelighed og respekt. Mange har allerede brugt meget tid på at forsøge at finde løsninger. Derfor skal det også give mening, kunne mærkes og være til at forstå.
                </p>
                <p className="leading-relaxed font-serif text-[#123F13] pt-2 text-base">
                  Jeg ønsker ikke bare, at du får det bedre i sessionen. Jeg ønsker også, at du forstår noget vigtigt om dig selv, som du kan tage med videre.
                </p>
                <div className="pt-6">
                  <Link 
                    to="/om-lars/se-hvordan-jeg-arbejder"
                    className="inline-flex items-center gap-2 text-[#8C2F12] font-bold text-xs uppercase tracking-widest hover:gap-3 transition-all"
                  >
                    Se mere om, hvordan jeg arbejder
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            </div>

            <div className="relative lg:sticky lg:top-32">
              <div className="relative aspect-[4/5] bg-[#E8DDCE] overflow-hidden rounded-sm shadow-xl">
                <img
                  src="/_assets/lars-hvid-skjorte.jpg"
                  alt="Lars Nissen Corell – Psykoterapeut MPF"
                  className="w-full h-full object-cover"
                />
                {/* Core Kvalitetsstempel */}
                <div className="absolute top-4 right-4 z-10 w-20 h-20 md:w-24 md:h-24 pointer-events-none">
                  <img
                    src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png"
                    alt="Core Kvalitetsstempel"
                    className="w-full h-full object-contain rotate-[-6deg] drop-shadow-[0_8px_16px_rgba(0,0,0,0.35)]"
                  />
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-white p-5 border border-[#E8DDCE] shadow-sm">
                  <Heart className="w-5 h-5 text-[#123F13] mb-2" />
                  <h4 className="font-serif text-[#123F13] text-sm mb-1">Rolig & nærværende</h4>
                  <p className="text-xs text-[#3B352F]/70">Direkte tilgang med fokus på tryghed.</p>
                </div>
                <div className="bg-white p-5 border border-[#E8DDCE] shadow-sm">
                  <ShieldCheck className="w-5 h-5 text-[#123F13] mb-2" />
                  <h4 className="font-serif text-[#123F13] text-sm mb-1">Diskretion</h4>
                  <p className="text-xs text-[#3B352F]/70">Alt, du fortæller, bliver behandlet fortroligt.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certificering & Erfaring (Mørkegrøn sektion inkl. MPF-logo med rolig rotation og links) */}
      <section className="py-20 lg:py-24 bg-[#123F13] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center text-white">Certificering & erfaring</h2>
            
            <div className="grid md:grid-cols-2 gap-10 items-start mb-12">
              <div className="space-y-5 text-white/85 leading-relaxed text-[15px]">
                <p>
                  Jeg er certificeret metakognitiv psykoterapeut fra{' '}
                  <a
                    href="https://www.neokognitivtinstitut.dk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#f1dcab] underline underline-offset-4 hover:text-white inline-flex items-center gap-1 font-medium"
                  >
                    Neokognitivt Institut
                    <ExternalLink size={13} className="inline opacity-80" />
                  </a>{' '}
                  (Level IV – højeste niveau). Jeg er desuden certificeret metakognitiv supervisor og gruppeleder, uddannet pædagog samt hypnoterapeut.
                </p>
                <p>
                  Min fireårige psykoterapeutuddannelse omfatter 900 timer, svarende til 1.200 lektioner, med undervisning, metodetræning, supervision, praktisk behandling og personlig faglig udvikling.
                </p>
                <p>
                  Med over 26 års erfaring i arbejdet med mennesker – både terapeutiske, pædagogiske og rådgivende opgaver – har jeg opbygget en solid forståelse for, hvad der virker i praksis, når livet slår knuder.
                </p>
              </div>
              <div className="bg-white/5 p-7 border border-white/10 rounded-sm">
                <h4 className="font-serif text-[#d2b266] mb-5 text-base">Her er, hvad jeg kan hjælpe dig med:</h4>
                <ul className="grid grid-cols-1 gap-3.5">
                  {[
                    "Angst og panik",
                    "Stress og udbrændthed",
                    "Depression og lavt selvværd",
                    "Søvnproblemer og tankemylder",
                    "ADHD, OCD og følgetilstande",
                    "Sorg, overbelastning og livskriser",
                    "Parterapi og relationscoaching"
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm items-center text-white/90">
                      <CheckCircle2 className="w-4 h-4 text-[#d2b266] shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Faglige uddannelseskort */}
            <div className="grid sm:grid-cols-3 gap-4 mb-14">
              <div className="bg-white/[0.06] border border-white/20 p-5 rounded-sm">
                <strong className="block font-serif text-[#f5e2b0] text-lg mb-1.5 font-normal">Fireårig uddannelse</strong>
                <span className="text-xs text-white/80 leading-relaxed block">
                  Metakognitiv psykoterapi · NKI Level IV · 900 timer / 1.200 lektioner
                </span>
              </div>
              <div className="bg-white/[0.06] border border-white/20 p-5 rounded-sm">
                <strong className="block font-serif text-[#f5e2b0] text-lg mb-1.5 font-normal">Supervisor & gruppeleder</strong>
                <span className="text-xs text-white/80 leading-relaxed block">
                  Certificeret af{' '}
                  <a
                    href="https://www.neokognitivtinstitut.dk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#f1dcab] underline underline-offset-2 hover:text-white"
                  >
                    Neokognitivt Institut
                  </a>
                </span>
              </div>
              <div className="bg-white/[0.06] border border-white/20 p-5 rounded-sm">
                <strong className="block font-serif text-[#f5e2b0] text-lg mb-1.5 font-normal">Pædagog & hypnoterapeut</strong>
                <span className="text-xs text-white/80 leading-relaxed block">
                  Faglig erfaring med mennesker, relationer og forandring
                </span>
              </div>
            </div>

            {/* Neokognitivt Institut Level IV sektion med guld-certificeringslogo */}
            <div className="pt-10 border-t border-white/25 mb-14">
              <div className="grid md:grid-cols-[240px_1fr] gap-8 md:gap-12 items-center">
                {/* NKI Level IV Logo boks */}
                <a
                  href="https://www.neokognitivtinstitut.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Besøg Neokognitivt Institut"
                  className="border border-[#d2b266]/50 bg-[radial-gradient(circle_at_50%_45%,rgba(139,119,56,0.18),transparent_72%)] rounded-lg p-6 min-h-[260px] md:min-h-[290px] flex flex-col items-center justify-center group transition-transform hover:scale-[1.02]"
                >
                  <img
                    src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/level-4-guld-png-1080x1080-2-1.png"
                    alt="Neokognitivt Institut – Certificeret Metakognitiv Psykoterapeut, Level IV Højeste Niveau"
                    className="w-48 h-48 md:w-56 md:h-56 object-contain drop-shadow-[0_8px_20px_rgba(0,0,0,0.35)]"
                  />
                  <span className="text-[11px] text-[#f1dcab] font-medium mt-3 flex items-center gap-1 group-hover:text-white transition-colors">
                    neokognitivtinstitut.dk <ExternalLink size={11} />
                  </span>
                </a>

                {/* NKI Level IV forklaring og officiel certificering */}
                <div className="space-y-4">
                  <span className="text-[#d2b266] uppercase tracking-widest text-[11px] font-bold block">
                    Neokognitivt Institut · Level IV (Højeste niveau)
                  </span>
                  <h3 className="text-2xl md:text-3xl font-serif text-[#f1dcab]">
                    Fireårig metakognitiv psykoterapeutuddannelse
                  </h3>
                  <p className="text-[14px] text-white/85 leading-relaxed">
                    Min fireårige psykoterapeutuddannelse ved{' '}
                    <a
                      href="https://www.neokognitivtinstitut.dk/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#f1dcab] underline underline-offset-4 hover:text-white inline-flex items-center gap-1 font-medium"
                    >
                      Neokognitivt Institut
                      <ExternalLink size={13} className="inline opacity-80" />
                    </a>{' '}
                    er gennemført på <strong>Level IV – instituttets højeste certificeringsniveau</strong>. Uddannelsen omfatter 900 timer, svarende til 1.200 lektioner, med undervisning, metodetræning, supervision, praktisk behandling og personlig faglig udvikling.
                  </p>
                  <p className="text-[14px] text-white/85 leading-relaxed">
                    Det er en evidensbaseret og grundig uddannelse, der sikrer en dyb metodisk forankring i metakognitiv terapi (MCT). Jeg er herudover certificeret metakognitiv supervisor og gruppeleder.
                  </p>
                  <div className="pt-2">
                    <a
                      href="https://www.neokognitivtinstitut.dk/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-wider uppercase border border-[#d9c997]/80 text-[#f4e9cb] bg-white/5 hover:bg-white/15 transition-colors"
                    >
                      <span>Besøg Neokognitivt Institut</span>
                      <ExternalLink size={13} className="shrink-0" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* MPF-sektion med det guldfarvede logo, 12s rotation og officielle links */}
            <div className="pt-10 border-t border-white/25">
              <div className="grid md:grid-cols-[240px_1fr] gap-8 md:gap-12 items-center">
                {/* Logo boks med rolig 12s 3D rotation og link til officiel MPF-profil */}
                <a
                  href="https://psykoterapeutforeningen.dk/psykoterapeuter/91679-Lars-Nissen-Corell"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Dansk Psykoterapeutforening – Se Lars Nissen Corells officielle MPF-profil"
                  className="border border-[#d2b266]/50 bg-[radial-gradient(circle_at_50%_45%,rgba(139,119,56,0.18),transparent_72%)] rounded-lg p-6 min-h-[280px] md:min-h-[320px] flex flex-col items-center justify-center group transition-transform hover:scale-[1.02]"
                >
                  <img
                    src="/_assets/mpf-guld.png"
                    alt="Psykoterapeut MPF – Dansk Psykoterapeutforening"
                    className="w-44 h-auto max-h-56 object-contain mpf-rotating-logo"
                  />
                  <span className="text-[11px] text-[#f1dcab] font-medium mt-3 flex items-center gap-1 group-hover:text-white transition-colors">
                    Officiel MPF-profil <ExternalLink size={11} />
                  </span>
                </a>

                {/* MPF forklaring og officielle links */}
                <div className="space-y-4">
                  <h3 className="text-2xl md:text-3xl font-serif text-[#f1dcab]">
                    Hvad betyder det, at jeg er psykoterapeut MPF?
                  </h3>
                  <p className="text-[14px] text-white/85 leading-relaxed">
                    <strong className="text-white font-semibold">MPF</strong> er en beskyttet medlemsbetegnelse knyttet til{' '}
                    <strong className="text-white font-semibold">Dansk Psykoterapeutforening</strong>. Foreningen stiller høje krav til medlemmernes uddannelse, faglige kvalifikationer og etiske praksis.
                  </p>
                  <p className="text-[14px] text-white/85 leading-relaxed">
                    For dig betyder det, at du kan undersøge min faglige baggrund og medlemskab hos foreningen. Det giver en ekstra tryghed og mulighed for at vurdere, hvem du ønsker at tale med, men det er ikke en garanti for et bestemt behandlingsresultat.
                  </p>
                  <p className="text-[14px] text-white/85 leading-relaxed">
                    Jeg lægger vægt på, at du bliver mødt med respekt, får en forståelig forklaring på behandlingen og kan stille spørgsmål undervejs. Mit medlemskab og min offentlige profil kan du selv kontrollere.
                  </p>

                  <div className="flex flex-wrap gap-3 pt-3">
                    <a
                      href="https://psykoterapeutforeningen.dk/psykoterapeuter/91679/Lars%20Nissen-Corell"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-wider uppercase border border-[#d9c997]/80 text-[#f4e9cb] bg-white/5 hover:bg-white/15 transition-colors"
                    >
                      <span>Se min officielle MPF-profil</span>
                      <ExternalLink size={13} className="shrink-0" />
                    </a>
                    <a
                      href="https://psykoterapeutforeningen.dk/psykoterapi/hvad-betyder-titlen-psykoterapeut-mpf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-wider uppercase border border-[#d9c997]/80 text-[#f4e9cb] bg-white/5 hover:bg-white/15 transition-colors"
                    >
                      <span>Hvad betyder Psykoterapeut MPF?</span>
                      <ExternalLink size={13} className="shrink-0" />
                    </a>
                    <a
                      href="https://psykoterapeutforeningen.dk/raadgivning-og-karriere/fag-og-etik/etikregler"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-wider uppercase border border-[#d9c997]/80 text-[#f4e9cb] bg-white/5 hover:bg-white/15 transition-colors"
                    >
                      <span>Læs de etiske regler</span>
                      <ExternalLink size={13} className="shrink-0" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-center text-lg md:text-xl font-serif text-white/95 max-w-2xl mx-auto mt-16 leading-relaxed">
              Med dokumenterede virksomme metoder hjælper jeg dig med at finde dit nærvær, styrke og handlekraft, så du igen kan leve det liv, du længes efter.
            </p>
          </div>
        </div>
      </section>

      {/* Tilgang Section */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-serif text-[#123F13] mb-3">Min tilgang: Rolig, enkel og effektiv</h2>
            <p className="text-[#3B352F]/70 text-sm">Skarp faglighed med varme værdier</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                title: "Skarp faglighed",
                desc: "Med over 26 års erfaring hjælper jeg dig med metoder, der virker, uden lange forløb eller snørklede teorier.",
                icon: GraduationCap
              },
              {
                title: "Mærkbar ro",
                desc: "Hypnosen hjælper dig med at give slip på overaktivitet i sind og krop, og finde tilbage til ro og nærvær.",
                icon: Sparkles
              },
              {
                title: "Fokus på mønstre",
                desc: "Vi arbejder ikke med at grave i fortiden, men med at forstå og ændre dine tanke- og handlemønstre.",
                icon: Shield
              },
              {
                title: "Praktiske redskaber",
                desc: "Du får enkle teknikker og øvelser, du kan bruge med det samme, både her i terapien, men i dit virkelige liv.",
                icon: MessageSquare
              }
            ].map((feature, i) => (
              <div key={i} className="p-7 bg-[#FAF7F2] border border-[#E8DDCE] flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-white flex items-center justify-center mb-5 shadow-sm">
                  <feature.icon className="w-6 h-6 text-[#123F13]" />
                </div>
                <h4 className="font-serif text-[#123F13] mb-3 text-base">{feature.title}</h4>
                <p className="text-xs text-[#3B352F]/75 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Klinik Section med det nye samlede billede fra V9 */}
      <section className="py-20 lg:py-24 bg-white border-t border-[#E8DDCE]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="text-3xl font-serif text-[#123F13]">Velkommen i klinikken i Valby</h2>
              <p className="text-[#3B352F]/80 leading-relaxed text-[15px]">
                Core Hypnose er et moderne og roligt terapisted midt i København, med god plads til dig. 
                Min klinik ligger i hjertet af Valby, tæt på Valby Langgade. Her tager jeg imod dig i et æstetisk indrettet og roligt rum, hvor du kan finde tryghed, nærvær og fordybelse.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                <div className="flex items-start gap-3.5">
                  <MapPin className="w-5 h-5 text-[#123F13] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-[#123F13] text-xs uppercase tracking-widest mb-1">Adresse</h4>
                    <p className="text-sm text-[#3B352F]/75">Valby Tingsted 7, 2500 Valby</p>
                  </div>
                </div>
                <div className="flex items-start gap-3.5">
                  <Clock className="w-5 h-5 text-[#123F13] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-[#123F13] text-xs uppercase tracking-widest mb-1">Adgang</h4>
                    <p className="text-sm text-[#3B352F]/75">Nem adgang med offentlig transport og parkering i området</p>
                  </div>
                </div>
              </div>

              <div className="p-7 bg-[#FAF7F2] border border-[#E8DDCE]">
                <h4 className="font-serif text-[#123F13] mb-3 text-lg">Terapi i rolige omgivelser</h4>
                <p className="text-sm text-[#3B352F]/75 leading-relaxed">
                  Klinikken er indrettet med sans for ro og æstetik. Her er ingen støj fra gangarealer eller trafik – alle vægge, døre og vinduer er lydisolerede, så du kan være helt til stede.
                </p>
              </div>
            </div>

            {/* Ét samlet klinikbillede som vist i V9 (viser klinikbygning, terapirum og Lars) */}
            <div className="w-full">
              <div className="overflow-hidden rounded-md border border-[#E8DDCE] shadow-lg bg-[#FAF7F2]">
                <img
                  src="/_assets/klinik-valby-nyt-samlet-billede.webp"
                  alt="Klinikken i Valby – Valby Tingsted 7, terapirum og Lars Nissen Corell"
                  className="w-full h-auto object-cover block"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* V9: Ti personlige spørgsmål og svar om Lars (fold-ud-afsnit) */}
      <section className="py-20 lg:py-24 bg-[#FBF9F5] border-t border-[#E8DDCE]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#8C2F12] block mb-3">
              Lidt mere om mig
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">
              10 spørgsmål om mennesket bag terapeuten
            </h2>
            <p className="text-sm md:text-base text-[#3B352F]/75 leading-relaxed">
              Du kan læse om min uddannelse og min tilgang ovenfor. Her fortæller jeg lidt mere om de erfaringer og overvejelser, som har formet mig og mit arbejde.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-3">
            {faqQuestions.map((item, idx) => {
              const isOpen = !!openItems[idx];
              return (
                <div
                  key={item.number}
                  className={`bg-white border transition-all rounded-md overflow-hidden ${
                    isOpen ? 'border-[#123F13]/30 shadow-md' : 'border-[#E8DDCE] hover:border-[#123F13]/20 shadow-sm'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(idx)}
                    className="w-full text-left p-5 md:p-6 flex items-start gap-4 justify-between transition-colors hover:bg-[#FAF7F2]/50"
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-start gap-4">
                      <span className="font-serif text-[#8C2F12] text-lg font-normal shrink-0 pt-0.5">
                        {item.number}
                      </span>
                      <h3 className="font-serif text-[#123F13] text-base md:text-lg font-medium leading-snug">
                        {item.q}
                      </h3>
                    </div>
                    <div className="shrink-0 text-[#123F13] mt-1 ml-2">
                      {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 md:px-6 pb-6 pt-2 border-t border-[#FAF7F2] text-sm md:text-[15px] text-[#3B352F]/85 leading-relaxed space-y-3.5 pl-12 md:pl-16">
                      {item.a.map((paragraph, pIdx) => (
                        <p key={pIdx}>{paragraph}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <p className="text-sm text-[#3B352F]/80">
              Har du praktiske spørgsmål til behandlingen?{' '}
              <Link to="/faq" className="text-[#123F13] underline font-semibold hover:text-[#8C2F12]">
                Se FAQ →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 lg:py-24 border-t border-[#E8DDCE] bg-[#FAF7F2]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif text-[#123F13] mb-6">Klar til at tage det første skridt?</h2>
          <p className="text-[#3B352F]/75 max-w-xl mx-auto mb-10 text-sm md:text-base leading-relaxed">
            Har du spørgsmål – eller vil du mærke, om Core Hypnose er det rette for dig? 
            Du kan booke din første behandlingssession eller sende mig en besked.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/bestil-tid" 
              className="px-10 py-4 bg-[#123F13] text-white text-[12px] font-bold tracking-[0.2em] uppercase hover:bg-[#1a5a1c] transition-all shadow-md"
            >
              Book første session
            </Link>
            <Link 
              to="/kontakt" 
              className="px-10 py-4 border border-[#123F13] text-[#123F13] text-[12px] font-bold tracking-[0.2em] uppercase hover:bg-white transition-all"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OmLars;
